import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap"

import GlobalInputSearch from "components/Common/GlobalInputSearch"
import FileViewerModal from "components/Common/FileViewerModal"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { downloadVehicleLcDocumentAcknowledgementPdf } from "api/vehicleLcDocumentApi"
import { useListParams } from "hooks/useListParams"
import { useVehicleLcDocumentGroups } from "hooks/useVehicleLcDocuments"
import { formatReadableDate } from "utils/formatDate"

import VehicleLcDocumentAddEdit from "./VehicleLcDocumentAddEdit"
import VehicleLcDocumentDetailsModal from "./VehicleLcDocumentDetailsModal"

const vehicleLabel = vehicle =>
  [vehicle?.stock_id, vehicle?.full_chassis || vehicle?.chassis_number]
    .filter(Boolean)
    .join(" - ")

const vehicleName = vehicle =>
  [vehicle?.brand?.name, vehicle?.model?.name, vehicle?.year?.name]
    .filter(Boolean)
    .join(" ")

const acknowledgementReference = group =>
  group?.lc_document_acknowledgement?.reference_number || "-"

const acknowledgementAttachments = group =>
  Array.isArray(group?.lc_document_acknowledgement?.attachments)
    ? group.lc_document_acknowledgement.attachments
    : []

const VehicleLcDocumentList = () => {
  document.title = "Vehicle LC Docs"

  const [addModal, setAddModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [detailsModal, setDetailsModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [viewerFile, setViewerFile] = useState(null)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
    }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useVehicleLcDocumentGroups(queryParams)

  const handleResetFilters = () => {
    resetParams()
  }

  const handleViewDocs = group => {
    setSelectedGroup(group)
    setDetailsModal(true)
  }

  const handleEditDocs = group => {
    setSelectedGroup(group)
    setEditModal(true)
  }

  const handleCloseDetails = () => {
    setDetailsModal(false)
    setSelectedGroup(null)
  }

  const handleCloseEdit = () => {
    setEditModal(false)
    setSelectedGroup(null)
  }

  const handlePrint = group => {
    downloadVehicleLcDocumentAcknowledgementPdf({
      vehicleStockId: group.id,
      onStart: () => setPdfLoadingId(group.id),
      onEnd: () => setPdfLoadingId(null),
    }).catch(() => toast.error("Failed to open acknowledgement PDF."))
  }

  const handleViewAttachment = attachment => {
    if (!attachment?.url) {
      toast.error("PDF preview is unavailable.")
      return
    }

    setViewerFile({
      url: attachment.url,
      name: attachment.original_name || attachment.filename || "PDF attachment",
    })
  }

  const columns = useMemo(
    () => [
      {
        header: "SL",
        cell: ({ row }) => acknowledgementReference(row.original),
      },
      {
        header: "Vehicle",
        cell: ({ row }) => {
          const group = row.original
          return (
            <div>
              <div className="fw-semibold">{vehicleLabel(group) || "-"}</div>
              <div className="text-muted small">{vehicleName(group) || "-"}</div>
            </div>
          )
        },
      },
      {
        header: "Total Docs",
        accessorKey: "documents_count",
        cell: ({ row }) => (
          <Badge className="badge-soft-info">
            {row.original.documents_count ?? 0}
          </Badge>
        ),
      },
      {
        header: "Created Date",
        accessorKey: "lc_document_acknowledgement.created_at",
        cell: ({ row }) =>
          row.original.lc_document_acknowledgement?.created_at
            ? formatReadableDate(row.original.lc_document_acknowledgement.created_at)
            : "-",
      },
      {
        header: "Attachments",
        cell: ({ row }) => {
          const attachments = acknowledgementAttachments(row.original)

          if (!attachments.length) {
            return <span className="text-muted">-</span>
          }

          return (
            <div className="d-flex flex-wrap gap-1">
              {attachments.map((attachment, index) => (
                <Button
                  key={attachment.id || attachment.url || index}
                  type="button"
                  color="link"
                  className="badge badge-soft-danger border-0 text-decoration-none"
                  onClick={() => handleViewAttachment(attachment)}
                  title={
                    attachment.original_name ||
                    attachment.filename ||
                    "PDF attachment"
                  }
                >
                  <i className="mdi mdi-file-pdf-box me-1" />
                  {attachment.original_name ||
                    attachment.filename ||
                    `PDF ${index + 1}`}
                </Button>
              ))}
            </div>
          )
        },
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const group = row.original
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleViewDocs(group)}
                  id={`view-vehicle-lc-docs-${group.id}`}
                >
                  <i className="mdi mdi-eye-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`view-vehicle-lc-docs-${group.id}`}
                >
                  View Docs
                </UncontrolledTooltip>
              </li>
              <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.EDIT]}>
                <li>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEditDocs(group)}
                    id={`edit-vehicle-lc-docs-${group.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip
                    placement="top"
                    target={`edit-vehicle-lc-docs-${group.id}`}
                  >
                    Edit Documents
                  </UncontrolledTooltip>
                </li>
              </CanView>
              <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.PRINT_ACKNOWLEDGEMENT]}>
                <li>
                  <Button
                    color="danger"
                    size="sm"
                    disabled={pdfLoadingId === group.id}
                    onClick={() => handlePrint(group)}
                    id={`print-vehicle-lc-docs-${group.id}`}
                  >
                    <i className={`mdi ${pdfLoadingId === group.id ? "mdi-loading mdi-spin" : "mdi-file-pdf-box"}`} />
                  </Button>
                  <UncontrolledTooltip
                    placement="top"
                    target={`print-vehicle-lc-docs-${group.id}`}
                  >
                    Print Acknowledgement
                  </UncontrolledTooltip>
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [pdfLoadingId]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Vehicle LC Docs" breadcrumbItem="Vehicle LC Docs">
      <div className="d-flex align-items-center">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Vehicles <Badge className="badge-soft-info">{paginationInfo?.total ?? 0}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.ADD]}>
            <Button color="primary" onClick={() => setAddModal(true)}>
              Add Document
            </Button>
          </CanView>
          <Button color="light" onClick={refetch} className="ms-1">
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && (
            <Button color="danger" onClick={handleResetFilters} className="ms-1">
              Reset Filters
            </Button>
          )}
        </div>
      </div>

      <div className="pb-3"></div>

      <Row>
        <Col md={5} lg={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <VehicleLcDocumentAddEdit
        modal={addModal}
        isEdit={false}
        editedData={null}
        toggleModal={() => setAddModal(prev => !prev)}
        onSaved={refetch}
      />

      <VehicleLcDocumentAddEdit
        modal={editModal}
        isEdit={false}
        editFull={true}
        editedData={selectedGroup}
        defaultVehicleId={selectedGroup?.id}
        toggleModal={handleCloseEdit}
        onSaved={refetch}
      />

      <VehicleLcDocumentDetailsModal
        isOpen={detailsModal}
        toggle={handleCloseDetails}
        group={selectedGroup}
        onChanged={refetch}
      />

      <FileViewerModal
        isOpen={!!viewerFile}
        onClose={() => setViewerFile(null)}
        fileUrl={viewerFile?.url || ""}
        fileName={viewerFile?.name || "PDF attachment"}
      />
    </PageWrapper>
  )
}

export default VehicleLcDocumentList
